import React, { useEffect, useState, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import APIErrorComponent from '../../../../../../components/_shared/Error/APIErrorComponent'
import { fetchIncidentAnalysisData } from '../../../../../../../utils/api/reportAPI'
import toasters from '../../../../../../../utils/toasters/Toasters'
import WmrcAvgResponseTable from './Table/WmrcAvgResponseTable'
import { useSearchParams } from 'react-router-dom'

const formatPayloadDates = (payload) => {
    if (!payload) return payload
    const formattedPayload = { ...payload }
    if (formattedPayload.startDate) {
        const startDate = new Date(formattedPayload.startDate)
        if (!isNaN(startDate)) {
            formattedPayload.startDate = startDate.toISOString().split('T')[0]
        }
    }
    if (formattedPayload.endDate) {
        const endDate = new Date(formattedPayload.endDate)
        if (!isNaN(endDate)) {
            formattedPayload.endDate = endDate.toISOString().split('T')[0]
        }
    }
    return formattedPayload
}

const WmrcAvgResponseTime = () => {
    const location = useLocation()
    const { token } = useSelector((state) => state?.auth)

    const storedTableOptions = JSON.parse(
        sessionStorage.getItem('wmrcTableOptions')
    ) || { rowPerPages: 10, activePage: 0 }
    const storedSort = JSON.parse(sessionStorage.getItem('wmrcSort')) || null

    const [sort, setSort] = useState(null)
    const [tableOptions, setTableOptions] = useState(storedTableOptions)
    
    // Persist table options and sort
    useEffect(() => {
        sessionStorage.setItem('wmrcTableOptions', JSON.stringify(tableOptions))
    }, [tableOptions])

    useEffect(() => {
        sessionStorage.setItem('wmrcSort', JSON.stringify(sort))
    }, [sort])

    const [searchParams] = useSearchParams()
    const [payload, setPayload] = useState(null)

    useEffect(() => {

        //  URL params
        const startDate = searchParams.get('startDate')
        const endDate = searchParams.get('endDate')
        const code = searchParams.get('code')

        if (startDate || endDate) {
            const urlPayload = { startDate, endDate, code }

            setPayload(urlPayload)

            sessionStorage.setItem(
                'wmrcAvgResponsePayload',
                JSON.stringify(urlPayload)
            )

            return
        }

        // location.state
        if (location.state) {
            setPayload(location.state)

            sessionStorage.setItem(
                'wmrcAvgResponsePayload',
                JSON.stringify(location.state)
            )

            return
        }

        // session fallback
        const stored = sessionStorage.getItem('wmrcAvgResponsePayload')

        if (stored) {
            setPayload(JSON.parse(stored))
        }

    }, [location.state, searchParams])

    const formattedPayload = formatPayloadDates(payload)

    // Fetch API Data
    const getWmrcAvgResponseData = useCallback(async () => {
        if (!formattedPayload) {
            return { data: [], Total: 0 }
        }

        let apiQueryParams = {
            per_page: tableOptions?.rowPerPages,
            page: tableOptions?.activePage + 1,
            export: 'N',
        }

        if (sort && sort?.columnName && sort.order) {
            apiQueryParams = {
                ...apiQueryParams,
                order: sort.order,
                sort: sort?.columnName,
            }
        }

        const response = await fetchIncidentAnalysisData({
            ...formattedPayload,
            ...apiQueryParams,
            export: 'N',
        })
        return response
    }, [sort, tableOptions, formattedPayload])

    const { isLoading, isFetching, data, isError, error } = useQuery(
        ['fetch-wmrc-avg-response', tableOptions, sort, formattedPayload],
        getWmrcAvgResponseData,
        {
            enabled: !!formattedPayload,
            retry: (failureCount, error) => {
                const status = error?.response?.status
                return failureCount < 1 && status >= 400 && status < 600
            },
            retryDelay: 1000,
        }
    )

    const handlePageClick = (event) => {
        setTableOptions((prev) => ({ ...prev, activePage: event.selected }))
    }

    const handleRowsPerPage = (e) => {
        setTableOptions((prev) => ({
            ...prev,
            rowPerPages: e.target.value,
            activePage: 0,
        }))
    }

    const handleColumnSort = (column, sortDirection) => {
        setSort({
            columnName: column?.sortField,
            order: sortDirection,
        })
        // Reset to first page when sorting changes
        setTableOptions((prev) => ({
            ...prev,
            activePage: 0,
        }))
    }

    // PDF Export
    const downloadPDF = async () => {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_BACKEND_SERVICE_URL}/reports/report_data`,
                { ...formattedPayload, export: 'N', pagination: 'false' },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            )

            const fullData = response?.data?.data || []

            if (!Array.isArray(fullData) || fullData.length === 0) {
                toasters({ type: 'error', message: 'No data found for PDF' })
                return
            }

            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: [297, 210],
            })

            // Title
            doc.setFontSize(18)
            doc.text('WMRC Average Response Time Report', 14, 22)

            // Date Range
            doc.setFontSize(12)
            doc.text(
                `Date Range: ${formattedPayload.startDate} - ${formattedPayload.endDate}`,
                14,
                30
            )

            // Table Columns
            const tableColumn = [
                'User ID',
                'WMRC Name',
                'Avg Response (Seconds)',
                'Avg Response (Time)',
            ]

            // Table Rows
            const tableRows = fullData.map((item) => [
                item?.user_id || '',
                item?.wmrc_name || '',
                item?.avg_response_seconds || '',
                item?.avg_response_time || '',
            ])

            doc.autoTable({
                head: [tableColumn],
                body: tableRows,
                startY: 40,
                theme: 'grid',
                headStyles: {
                    fillColor: [0, 81, 242],
                    textColor: [255, 255, 255],
                    fontSize: 9,
                },
                bodyStyles: { fontSize: 10, cellPadding: 3 },
                alternateRowStyles: { fillColor: [245, 245, 245] },
            })

            // Totals (optional)
            const totalUsers = fullData.length
            const finalY = doc.lastAutoTable.finalY + 10
            doc.setFontSize(12)
            doc.text(`Total Users: ${totalUsers}`, 14, finalY)

            doc.save('wmrc-avg-response-report.pdf')
        } catch (error) {
            console.error('PDF Export Error:', error)
            toasters({ type: 'error', message: 'Error generating PDF' })
        }
    }

    // Excel Export
    const downloadFile = (csvString, fileName = 'download') => {
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${fileName}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }

    const ExcelWmrcAvgResponseReport = async () => {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_BACKEND_SERVICE_URL}/reports/report_data`,
                {
                    ...formattedPayload,
                    export: 'Y',
                    pagination: 'false',
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + token,
                    },
                }
            )

            if (!response?.data || typeof response.data !== 'string') {
                toasters({ type: 'error', message: 'No data found to export' })
                return
            }

            downloadFile(response.data, 'wmrc-avg-response-report')
        } catch (error) {
            toasters({
                type: 'error',
                message:
                    error?.response?.data?.message ||
                    'Error downloading Excel file',
            })
        }
    }

    const pageCount = Math.ceil((data?.total || 0) / tableOptions.rowPerPages)

    return (
        <>
            {isError ? (
                <APIErrorComponent error={error} />
            ) : (
                <WmrcAvgResponseTable
                    isLoading={isLoading || isFetching}
                    data={data?.data || []}
                    totaldata={data?.total}
                    tableOptions={tableOptions}
                    handlePageClick={handlePageClick}
                    handleRowsPerPage={handleRowsPerPage}
                    handleColumnSort={handleColumnSort}
                    downloadPDF={downloadPDF}
                    downloadExcel={ExcelWmrcAvgResponseReport}
                    pageCount={pageCount}
                    reportData={formattedPayload}
                    reportCode={payload?.code}
                    reportTableData={data?.data}
                    generatePDFFunction={downloadPDF}
                />
            )}
        </>
    )
}

export default WmrcAvgResponseTime
